import type { TaskNode } from "./types";
import { progress, rollup, leafDoneCounts, treeDoneCounts } from "./tree";
import { fmt } from "./format";

export interface ReportCtx {
  projectName: string;
  versions: string[];
  now?: string; // ISO timestamp (injected for tests); default = today
}

// shareable plain-text report: totals + progress + active task tree with estimate,
// version and completion. Disabled tasks are excluded; done tags omitted when nothing done.
export function buildReport(tree: TaskNode[], ctx: ReportCtx): string {
  const p = progress(tree);
  const anyDone = treeDoneCounts(tree).doneN > 0; // is any task actually finished?
  let qCount = 0; // active tasks flagged with open questions
  (function count(list: TaskNode[]) {
    list.forEach((n) => { if (n.disabled) return; if (n.questions) qCount++; count(n.children); });
  })(tree);

  const lines: string[] = [];
  lines.push((ctx.projectName || "Untitled project").toUpperCase());
  lines.push(anyDone
    ? "Total: " + fmt(p.total) + "d   |   " + p.pct + "% done (" + fmt(p.done) + "d of " + fmt(p.total) + "d)"
    : "Total: " + fmt(p.total) + "d");
  if (ctx.versions.length) lines.push("Versions: " + ctx.versions.join(", "));
  if (qCount) lines.push("Open questions: " + qCount + " — need discussion (marked ❓)");
  lines.push("Generated: " + (ctx.now || new Date().toISOString()).slice(0, 10));
  lines.push("");

  (function walk(list: TaskNode[], prefix: string, depth: number) {
    list.filter((n) => !n.disabled).forEach((n, i) => {
      const num = prefix ? prefix + "." + (i + 1) : String(i + 1);
      const meta: string[] = [fmt(rollup(n)) + "d"];
      if (n.version) meta.push(n.version);
      if (n.questions) meta.push("❓ TBD");
      if (anyDone) {
        if (n.children.length === 0) {
          if (n.done) meta.push("✓ done");
        } else {
          const c = leafDoneCounts(n);
          meta.push(c.totalN && c.doneN === c.totalN ? "✓ all done" : c.doneN + "/" + c.totalN + " done");
        }
      }
      lines.push("  ".repeat(depth) + num + ". " + n.name + " — " + meta.join(" · "));
      if (n.children.length) walk(n.children, num, depth + 1);
    });
  })(tree, "", 0);

  return lines.join("\n");
}
